// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { DataTableCssOverrides } from "@awc/MobiusOverrides/DataTable/DataTable";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { xBaseTheme } from "@awc/XDL/types";
import { color, xScreen } from "@awc/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import DataTablePresentationPropsDefault from "@insite/mobius/DataTable/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

const stickyMode = () => css`
    ${xScreen("lte", "sm")} {
        position: sticky;
        left: 0;
        z-index: 2;
        background-clip: padding-box;
        background: inherit;
        > ${TypographyStyle} {
            display: block;
            max-width: 140px;
            text-overflow: ellipsis;
            overflow: hidden;
        }
    }
`;

export default {
    defaultProps: {
        ...DataTablePresentationPropsDefault,
        mergeCss: true,
        cssOverrides: {
            header: css`
                background: ${color("common.ui3")};
                padding: 8px 12px 8px 8px;
                height: initial;
                line-height: normal;
                border: 1px solid ${color("common.ui2")};
            `,
            head: css`
                background: ${color("common.ui3")};

                th:first-child {
                    ${stickyMode()}
                }
            `,
            cell: css`
                padding: 8px;
                height: initial;
                line-height: normal;
                border: 1px solid ${color("common.ui2")};
                ${StyledButton} > ${TypographyStyle} {
                    font-size: ${fontConfig.fontSize.regular}px;
                    line-height: ${fontConfig.lineHeight.singleLineRegular};
                }
                &:first-child {
                    ${stickyMode()}
                }
            `,
            row: css`
                background: ${color("common.white")};
            `,
            evenRow: css`
                background: ${color("common.ui2")};
            `,
        } as DataTableCssOverrides,
        headerTypographyProps: {
            mergeCss: true,
            size: fontConfig.fontSize.small,
            weight: fontConfig.weight.bold,
            color: "text.main",
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.lineHeight.singleLineSmall};
            `,
        },
        cellTypographyProps: {
            mergeCss: true,
            size: fontConfig.fontSize.small,
            weight: fontConfig.weight.regular,
            css: css`
                letter-spacing: 0;
                line-height: ${fontConfig.lineHeight.singleLineSmall};
            `,
        },
        sortClickableProps: {
            css: css`
                width: 100%;
                height: 100%;
                &:hover {
                    ${TypographyStyle}, ${IconWrapper} {
                        color: ${color("common.ui8")};
                    }
                }
            `,
        },
        sortIconProps: {
            size: 12,
            color: "text.main",
            css: css`
                margin-left: auto;
            `,
        },
        css: css`
            td[class^="DataTableCellBase"] {
                & > ${TypographyStyle}:empty:after {
                    content: "- -";
                }
            }
        `,
    },
} as xBaseTheme["dataTable"];
